import { Zap, TrendingUp, Target, Star, ArrowUp, ArrowRight, Sparkles } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useGSCTopQueries, useGSCTopPages } from '@/hooks/useGSCData';
import { useAuthStore } from '@/stores/auth-store';
import { useMemo } from 'react';

interface Opportunity {
  type: 'quick-win' | 'striking-distance' | 'high-volume' | 'ctr-boost';
  title: string;
  description: string;
  query?: string;
  page?: string;
  metrics: {
    clicks: number;
    impressions: number;
    ctr: number;
    position: number;
  };
  potentialGain: string;
  priority: 'high' | 'medium' | 'low';
  action: string;
}

/**
 * Quick Wins View - Actionable SEO opportunities
 * Identifies and prioritizes opportunities for improvement
 */
export function QuickWinsView() {
  const { activePropertyId, properties } = useAuthStore();
  const activeProperty = properties.find((p) => p.id === activePropertyId);
  const hasGSC = !!activeProperty?.gscSite;

  const { data: queries, isLoading: queriesLoading } = useGSCTopQueries(50);
  const { data: _pages, isLoading: pagesLoading } = useGSCTopPages(50);

  const isLoading = queriesLoading || pagesLoading;

  // Generate opportunities from data
  const opportunities = useMemo(() => {
    if (!queries) return [];

    const opps: Opportunity[] = [];

    for (const q of queries) {
      // Quick Win: Position 4-10, high impressions, low CTR
      if (q.position >= 4 && q.position <= 10 && q.impressions > 100 && q.ctr < 3) {
        opps.push({
          type: 'quick-win',
          title: 'Low CTR in Top 10',
          description: `"${q.query}" ranks on page 1 but has low click-through rate`,
          query: q.query,
          metrics: q,
          potentialGain: `+${Math.round(q.impressions * 0.03)} clicks/month`,
          priority: 'high',
          action: 'Improve title & meta description',
        });
      }

      // Striking Distance: Position 11-20, good impressions
      if (q.position > 10 && q.position <= 20 && q.impressions > 50) {
        const potentialClicks = Math.round(q.impressions * 0.05);
        opps.push({
          type: 'striking-distance',
          title: 'Just Off Page 1',
          description: `"${q.query}" is close to page 1 - small improvements can have big impact`,
          query: q.query,
          metrics: q,
          potentialGain: `+${potentialClicks} clicks if moved to page 1`,
          priority: q.impressions > 200 ? 'high' : 'medium',
          action: 'Add content, build internal links',
        });
      }

      // High Volume, Poor Position
      if (q.impressions > 500 && q.position > 20) {
        opps.push({
          type: 'high-volume',
          title: 'High Volume Opportunity',
          description: `"${q.query}" has significant search volume but ranks poorly`,
          query: q.query,
          metrics: q,
          potentialGain: `${q.impressions.toLocaleString()} monthly searches`,
          priority: 'medium',
          action: 'Create dedicated content',
        });
      }

      // Top Position, Low CTR
      if (q.position <= 3 && q.ctr < 5 && q.impressions > 50) {
        opps.push({
          type: 'ctr-boost',
          title: 'CTR Below Average for Position',
          description: `"${q.query}" ranks #${Math.round(q.position)} but CTR is below expected`,
          query: q.query,
          metrics: q,
          potentialGain: `+${Math.round(q.impressions * 0.05)} clicks with better CTR`,
          priority: 'medium',
          action: 'Test rich snippets, improve meta',
        });
      }
    }

    // Sort by priority and potential impact
    return opps.sort((a, b) => {
      const priorityOrder = { high: 0, medium: 1, low: 2 };
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      return b.metrics.impressions - a.metrics.impressions;
    });
  }, [queries]);

  // Group by type
  const quickWins = opportunities.filter((o) => o.type === 'quick-win');
  const strikingDistance = opportunities.filter((o) => o.type === 'striking-distance');
  const highVolume = opportunities.filter((o) => o.type === 'high-volume');
  const ctrBoost = opportunities.filter((o) => o.type === 'ctr-boost');

  if (!hasGSC) {
    return (
      <div className="flex h-full items-center justify-center text-muted-foreground">
        <div className="text-center">
          <Zap className="h-12 w-12 mx-auto mb-4 opacity-50" />
          <p>No Search Console property connected</p>
          <p className="text-sm mt-2">Connect GSC to discover SEO opportunities</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="text-2xl font-semibold text-foreground flex items-center gap-2">
          <Sparkles className="h-6 w-6 text-warning" />
          Quick Wins & Opportunities
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Actionable insights to improve your search performance
        </p>
      </div>

      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground animate-pulse">
          Analyzing opportunities...
        </div>
      ) : opportunities.length === 0 ? (
        <div className="text-center py-12">
          <Star className="h-12 w-12 mx-auto mb-4 text-success" />
          <p className="text-foreground font-medium">Great job!</p>
          <p className="text-sm text-muted-foreground mt-2">
            No obvious quick wins found. Your search performance looks solid!
          </p>
        </div>
      ) : (
        <>
          {/* Summary */}
          <div className="grid grid-cols-4 gap-4">
            <OpportunitySummaryCard
              icon={Zap}
              count={quickWins.length}
              label="Quick Wins"
              description="Page 1, low CTR"
              color="success"
            />
            <OpportunitySummaryCard
              icon={Target}
              count={strikingDistance.length}
              label="Striking Distance"
              description="Just off page 1"
              color="warning"
            />
            <OpportunitySummaryCard
              icon={TrendingUp}
              count={highVolume.length}
              label="High Volume"
              description="Big search volume"
              color="blue"
            />
            <OpportunitySummaryCard
              icon={ArrowUp}
              count={ctrBoost.length}
              label="CTR Boost"
              description="Below avg for position"
              color="purple"
            />
          </div>

          {/* Opportunity Cards */}
          <div className="space-y-6">
            {quickWins.length > 0 && (
              <OpportunitySection
                title="Quick Wins"
                subtitle="High-impact, low-effort improvements"
                icon={Zap}
                color="success"
                opportunities={quickWins.slice(0, 5)}
              />
            )}

            {strikingDistance.length > 0 && (
              <OpportunitySection
                title="Striking Distance"
                subtitle="Close to page 1 - worth the push"
                icon={Target}
                color="warning"
                opportunities={strikingDistance.slice(0, 5)}
              />
            )}

            {highVolume.length > 0 && (
              <OpportunitySection
                title="High Volume Opportunities"
                subtitle="Significant search volume waiting to be captured"
                icon={TrendingUp}
                color="blue"
                opportunities={highVolume.slice(0, 5)}
              />
            )}

            {ctrBoost.length > 0 && (
              <OpportunitySection
                title="CTR Improvement"
                subtitle="Already ranking well, just need better click-through"
                icon={ArrowUp}
                color="purple"
                opportunities={ctrBoost.slice(0, 5)}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}

interface OpportunitySummaryCardProps {
  icon: React.ComponentType<{ className?: string }>;
  count: number;
  label: string;
  description: string;
  color: 'success' | 'warning' | 'blue' | 'purple';
}

function OpportunitySummaryCard({ icon: Icon, count, label, description, color }: OpportunitySummaryCardProps) {
  const colorStyles = {
    success: 'border-l-success bg-success/5 text-success',
    warning: 'border-l-warning bg-warning/5 text-warning',
    blue: 'border-l-blue-500 bg-blue-500/5 text-blue-400',
    purple: 'border-l-purple-500 bg-purple-500/5 text-purple-400',
  };

  return (
    <div className={cn('glass-panel p-4 border-l-4', colorStyles[color])}>
      <div className="flex items-center gap-2">
        <Icon className="h-5 w-5" />
        <span className="text-3xl font-bold">{count}</span>
      </div>
      <p className="text-sm font-medium text-foreground mt-1">{label}</p>
      <p className="text-xs text-muted-foreground">{description}</p>
    </div>
  );
}

interface OpportunitySectionProps {
  title: string;
  subtitle: string;
  icon: React.ComponentType<{ className?: string }>;
  color: 'success' | 'warning' | 'blue' | 'purple';
  opportunities: Opportunity[];
}

function OpportunitySection({ title, subtitle, icon: Icon, color, opportunities }: OpportunitySectionProps) {
  const borderColors = {
    success: 'border-l-success',
    warning: 'border-l-warning',
    blue: 'border-l-blue-500',
    purple: 'border-l-purple-500',
  };

  const iconColors = {
    success: 'text-success',
    warning: 'text-warning',
    blue: 'text-blue-400',
    purple: 'text-purple-400',
  };

  return (
    <div className="glass-panel p-6">
      <div className="flex items-center gap-3 mb-4">
        <Icon className={cn('h-6 w-6', iconColors[color])} />
        <div>
          <h2 className="text-lg font-medium text-foreground">{title}</h2>
          <p className="text-sm text-muted-foreground">{subtitle}</p>
        </div>
      </div>

      <div className="space-y-3">
        {opportunities.map((opp, idx) => (
          <OpportunityCard key={idx} opportunity={opp} borderColor={borderColors[color]} />
        ))}
      </div>
    </div>
  );
}

interface OpportunityCardProps {
  opportunity: Opportunity;
  borderColor: string;
}

function OpportunityCard({ opportunity, borderColor }: OpportunityCardProps) {
  return (
    <div className={cn('p-4 rounded-lg bg-muted/30 border-l-4', borderColor)}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className={cn(
              'text-xs font-medium px-2 py-0.5 rounded',
              opportunity.priority === 'high' ? 'bg-success/20 text-success' :
              opportunity.priority === 'medium' ? 'bg-warning/20 text-warning' :
              'bg-muted text-muted-foreground'
            )}>
              {opportunity.priority.toUpperCase()}
            </span>
          </div>
          <p className="text-sm text-foreground mt-2">{opportunity.description}</p>
          <div className="flex items-center gap-4 mt-3 text-xs text-muted-foreground">
            <span>Pos: <strong className="text-foreground">{opportunity.metrics.position.toFixed(1)}</strong></span>
            <span>CTR: <strong className="text-foreground">{opportunity.metrics.ctr.toFixed(1)}%</strong></span>
            <span>Impr: <strong className="text-foreground">{opportunity.metrics.impressions.toLocaleString()}</strong></span>
          </div>
        </div>
        <div className="text-right flex-shrink-0">
          <div className="text-sm font-medium text-success">{opportunity.potentialGain}</div>
          <div className="flex items-center gap-1 mt-2 text-xs text-primary">
            <ArrowRight className="h-3 w-3" />
            {opportunity.action}
          </div>
        </div>
      </div>
    </div>
  );
}
